import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Star, ArrowRight, Globe } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEO';
import { SEO_KEYWORDS } from '../components/SEO';
import { TIER_1_CITIES, TIER_2_CITIES } from '../data/cities';

const Cities = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="cities-page">
      <SEOHead
        title="Human on Rent in All Indian Cities - Tier 1 & Tier 2 | HumanOnRent.AI"
        description="Rent verified humans in Mumbai, Delhi, Bangalore, Hyderabad, Chennai, Kolkata, Pune & 18+ Tier 2 cities. 1 Lac+ providers. AI-powered matching across every Indian pin code."
        keywords={SEO_KEYWORDS.cities}
        url="https://humanonrent.ai/cities"
      />
      <span className="sr-only-seo">
        human on rent near me, boyfriend on rent in mumbai delhi bangalore, girlfriend on rent in india cities,
        hire human in tier 1 cities, rent a friend near me india, humanonrent.ai cities
      </span>

      <Header />

      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              Available All Over India
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Cities We <span className="gradient-text">Serve</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              HumanOnRent.AI operates across every pin code in India. 
              1 Lac+ verified humans ready to serve in Tier 1 and Tier 2 cities.
            </p>
          </div>

          {/* Tier 1 Cities */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Tier 1 -- Metro Cities</h2>
                <p className="text-gray-500 text-sm">Highest demand, maximum providers</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {TIER_1_CITIES.map((city) => (
                <Link
                  key={city.slug}
                  to={`/cities/${city.slug}`}
                  className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-orange-200 hover:shadow-lg transition-all"
                  data-testid={`city-${city.slug}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">{city.name}</h3>
                      <p className="text-sm text-gray-500">{city.state}</p>
                    </div>
                    <span className="text-orange-600 font-medium text-sm flex items-center gap-1 mt-1">
                      View <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{city.desc}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-gray-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> PIN: {city.pins}
                    </span>
                    <span className="bg-gray-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Globe className="w-3 h-3" /> All Pin Codes
                    </span>
                    <span className="text-orange-600 font-medium ml-auto flex items-center gap-1">
                      View Details <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Tier 2 Cities */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Tier 2 -- Growing Cities</h2>
                <p className="text-gray-500 text-sm">Rapidly expanding provider network</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {TIER_2_CITIES.map((city) => (
                <Link
                  key={city.slug}
                  to={`/cities/${city.slug}`}
                  className="group bg-white rounded-2xl p-4 text-center border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all"
                  data-testid={`city-${city.slug}`}
                >
                  <h3 className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors">{city.name}</h3>
                  <p className="text-xs text-gray-500">{city.state}</p>
                  <p className="text-xs text-gray-400 mt-2">PIN: {city.pins}</p>
                  <span className="text-orange-600 text-xs font-medium mt-2 inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* All India Coverage */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-10 text-center text-white mb-12">
            <Globe className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Not Listed Here? We're Still Available!</h2>
            <p className="text-white/80 text-lg mb-6 max-w-2xl mx-auto">
              HumanOnRent.AI covers every pin code in India. Whether you're in a metro city, 
              a tier 3 town, or a rural area -- our AI matches you with the nearest available provider.
            </p>
            <Link 
              to="/auth" 
              className="inline-flex items-center gap-2 bg-white text-purple-700 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all"
              data-testid="cities-cta-btn"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* SEO Content */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Human on Rent in India -- Available Everywhere</h2>
            <div className="text-gray-600 space-y-3 text-sm leading-relaxed">
              <p>
                HumanOnRent.AI is India's first AI-driven platform for renting humans for professional services. 
                Our platform is available across all states and union territories of India, covering every pin code 
                from Jammu & Kashmir to Kanyakumari, from Gujarat to the Northeast.
              </p>
              <p>
                Whether you need a travel partner in Shimla, an elder care provider in Varanasi, a shopping 
                partner in Jaipur, or an event partner in Goa -- our AI-powered matching ensures you find the 
                perfect match near you. With 1 Lac+ registered humans and growing, we're making professional 
                human services accessible to every corner of India.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Cities;
